import { useEffect, useRef, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { loadSquareSdk } from "@/lib/square";

// Renders Square's secure card entry fields and tokenizes on submit.
// Calls onToken(sourceId) with the single-use payment token.
export function SquareCardForm({ config, onToken, busy }) {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let card;
    let cancelled = false;
    (async () => {
      try {
        const Square = await loadSquareSdk(config.environment);
        if (!Square) throw new Error("SDK unavailable");
        const payments = Square.payments(config.application_id, config.location_id);
        card = await payments.card();
        if (cancelled) return;
        await card.attach(containerRef.current);
        cardRef.current = card;
        setReady(true);
      } catch (e) {
        setErr("Could not load the secure payment form. Please refresh and try again.");
      }
    })();
    return () => {
      cancelled = true;
      try { card && card.destroy && card.destroy(); } catch (_) {}
    };
  }, [config]);

  const submit = async () => {
    if (!cardRef.current) return;
    setErr("");
    setSubmitting(true);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status === "OK") {
        await onToken(result.token);
      } else {
        setErr(result.errors?.[0]?.message || "Please check your card details.");
      }
    } catch (e) {
      setErr("Card could not be validated. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        data-testid="square-card-container"
        className="min-h-[90px] rounded-lg border border-slate-200 p-3"
      />
      {!ready && !err && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading secure card form…
        </div>
      )}
      {err && <p data-testid="square-card-error" className="text-sm text-red-600">{err}</p>}
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <Lock className="h-3.5 w-3.5" /> Payments secured by Square. Sandbox test card: 4111 1111 1111 1111.
      </div>
      <Button
        data-testid="save-card-btn"
        onClick={submit}
        disabled={!ready || submitting || busy}
        className="w-full bg-blue-600 hover:bg-blue-700"
      >
        {submitting || busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save card"}
      </Button>
    </div>
  );
}
